import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Shirt, MapPin, GraduationCap, Ruler, Clock, Loader2 } from "lucide-react";
import { Sports } from "@/api/sports";
import { AuthExpiredError } from "@/api/client";
import { Skeleton } from "@/components/ui-kit";
import { DataState, SourceNote } from "@/components/DataState";
import { CURRENT_SEASON } from "@/lib/sports";
import { runPlayerPolling } from "@/lib/playerPolling";

// Humanize a raw stat key for display (e.g. passing_yards → Passing Yards).
const humanize = (k) => String(k).replace(/[_.]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function StatGrid({ stats, testid }) {
  const entries = Object.entries(stats || {}).filter(([, v]) => v !== null && v !== undefined && typeof v !== "object");
  if (!entries.length) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5" data-testid={testid}>
      {entries.map(([k, v]) => (
        <div key={k} className="rounded-lg bg-black/25 border border-white/10 p-3" data-testid={`stat-${k}`}>
          <div className="text-[10px] tracking-wide text-white/40 uppercase truncate">{humanize(k)}</div>
          <div className="font-display text-xl text-white tabnums mt-0.5">{String(v)}</div>
        </div>
      ))}
    </div>
  );
}

function IdentityBit({ icon: Icon, label }) {
  if (!label) return null;
  return <span className="inline-flex items-center gap-1.5 text-white/60 text-sm"><Icon size={14} className="text-white/35" /> {label}</span>;
}

export default function PlayerDetailPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const season = params.get("season") || CURRENT_SEASON;
  const [res, setRes] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading | processing | done | timeout | error
  const [reloadKey, setReloadKey] = useState(0);

  const load = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setRes(null);
    setPhase("loading");

    runPlayerPolling({
      signal: controller.signal,
      fetchPlayer: async () => {
        const r = await Sports.player(id, season);
        // Surface the player identity + processing state immediately so the
        // page never looks blank while the cache warms.
        if (!cancelled && r && r.state === "processing") { setPhase("processing"); setRes(r); }
        return r;
      },
    })
      .then(({ result, timedOut, cancelled: c }) => {
        if (c || cancelled) return;
        setRes(result);
        setPhase(timedOut ? "timeout" : "done");
      })
      .catch((e) => {
        if (cancelled || e instanceof AuthExpiredError) return;
        setPhase("error");
        setRes({ state: "error", player: null, seasonStats: null, gameLog: [] });
      });

    return () => { cancelled = true; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, season, reloadKey]);

  if (!res) return <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8"><Skeleton className="h-40 mb-4" /><Skeleton className="h-64" /></div>;

  if (res.state === "not_found") {
    return <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8" data-testid="player-not-found"><DataState state="empty" title="Player not found" detail="No player matches this id in the current-season feed." /></div>;
  }
  if (res.state === "error" || res.state === "feature_unavailable") {
    return <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8"><DataState state={res.state} onRetry={load} title={res.state === "error" ? "Couldn't load player" : "Player data not available yet"} detail={res.state === "feature_unavailable" ? "This is being connected to live data. Check back soon." : undefined} /></div>;
  }
  if (res.state === "unavailable") {
    return (
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8">
        <DataState state="feature_unavailable" title="Player coverage unavailable" detail={res.coverage?.unavailable_reason || "This player's current-season data isn't supported upstream yet."} data-testid="player-unavailable" />
      </div>
    );
  }

  const p = res.player || {};
  const jersey = p.jersey != null ? `#${p.jersey}` : null;
  const teamLine = [p.teamName || p.teamAbbr, p.position, jersey].filter(Boolean).join(" · ");
  const physical = [p.height, p.weight].filter(Boolean).join(", ");
  const isProcessing = res.state === "processing";
  const processingTimedOut = isProcessing && phase === "timeout";
  const hasStats = !!res.seasonStats && Object.keys(res.seasonStats).length > 0;
  const hasLog = res.gameLog.length > 0;
  // Honest "partial" state: verified identity but thin stat/log coverage (common
  // for NCAAF where complete data isn't supported upstream). NOT while processing.
  const partial = !isProcessing && !hasStats && !hasLog;

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-sm mb-4" data-testid="player-back"><ArrowLeft size={16} /> Back</button>

      <div className="tt-panel-3 rounded-2xl p-6" data-testid="player-header">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="font-display text-3xl sm:text-4xl text-white leading-tight" data-testid="player-name">{p.name || "Unnamed player"}</div>
            <div className="text-white/50 text-sm mt-1">{[p.league, teamLine].filter(Boolean).join(" · ") || "—"}
              {p.teamId && <> · <Link to={`/team/${p.teamId}`} className="text-[#4ea152] hover:underline" data-testid="player-team-link">Team</Link></>}
            </div>
          </div>
          {res.state === "stale" && <span className="text-[10px] font-display px-2 py-1 rounded bg-[#3a2f14] text-[#F4B740]" data-testid="player-stale-badge">CACHED · may be out of date</span>}
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4">
          <IdentityBit icon={MapPin} label={p.position ? `Position: ${p.position}` : null} />
          <IdentityBit icon={Shirt} label={jersey ? `Jersey ${jersey}` : null} />
          <IdentityBit icon={Ruler} label={physical || null} />
          <IdentityBit icon={GraduationCap} label={p.college ? `College: ${p.college}` : null} />
          <IdentityBit icon={Clock} label={p.experience != null ? `Experience: ${p.experience}` : null} />
        </div>
      </div>
      <SourceNote timestamp={res.coverage?.refreshed_at} coverage={`Season ${res.season} · verified provider feed`} />

      {isProcessing && !processingTimedOut && (
        <div className="mt-5 rounded-lg border border-[#14324a] bg-[#0e1d29] px-3 py-2.5 text-[12px] text-[#7fb2e6] flex items-center gap-2" data-testid="player-processing-note">
          <Loader2 size={14} className="animate-spin shrink-0" /> Fetching verified stats… the provider is warming this player's season and game-log cache.
        </div>
      )}
      {processingTimedOut && (
        <div className="mt-5 rounded-lg border border-[#3a2f14] bg-[#241d0e] px-3 py-2.5 text-[12px] text-[#F4B740] flex items-center justify-between gap-3" data-testid="player-processing-timeout">
          <span>Still processing — verified stats aren't ready yet. This is taking longer than usual.</span>
          <button onClick={load} className="shrink-0 text-[11px] font-display px-2.5 py-1 rounded bg-[#F4B740] text-black hover:brightness-110" data-testid="player-processing-retry">Retry</button>
        </div>
      )}

      {partial && (
        <div className="mt-5 rounded-lg border border-[#3a2f14] bg-[#241d0e] px-3 py-2.5 text-[12px] text-[#F4B740]" data-testid="player-partial-note">
          Verified identity only. Current-season stats and game log for this player aren't available from the provider yet
          {p.league === "NCAA" ? " — complete NCAAF player data isn't supported upstream." : "."}
        </div>
      )}

      <section className="mt-6">
        <h2 className="font-display tracking-wide text-lg text-white mb-3">Current-season stats</h2>
        {hasStats
          ? <StatGrid stats={res.seasonStats} testid="player-season-stats" />
          : <div className="tt-panel rounded-xl p-6 text-center text-white/40 text-sm" data-testid="player-stats-empty">{isProcessing ? "Fetching verified stats…" : `No verified season stats available for ${res.season} yet.`}</div>}
      </section>

      <section className="mt-6">
        <h2 className="font-display tracking-wide text-lg text-white mb-3">Game log</h2>
        {hasLog ? (
          <div className="space-y-2" data-testid="player-game-log">
            {res.gameLog.map((g, i) => {
              const statBits = Object.entries(g.stats || {}).filter(([, v]) => v !== null && v !== undefined && typeof v !== "object").slice(0, 4)
                .map(([k, v]) => `${humanize(k)} ${v}`).join(" · ");
              const meta = [g.date ? new Date(g.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null, g.opponent ? `vs ${g.opponent}` : null].filter(Boolean).join(" · ");
              const inner = (
                <>
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{meta || "Game"}</div>
                    {statBits && <div className="text-[11px] text-white/45 truncate mt-0.5">{statBits}</div>}
                  </div>
                  <span className="text-xs tabnums shrink-0 flex items-center gap-2">
                    {g.result && <span className={g.result === "W" ? "text-[#4ea152]" : g.result === "L" ? "text-[#ff6b60]" : "text-white/50"}>{g.result}</span>}
                    {g.status && <span className="text-white/40 uppercase">{g.status}</span>}
                  </span>
                </>
              );
              return g.gameId ? (
                <Link key={g.gameId || i} to={`/game/${g.gameId}`} data-testid={`player-log-row-${g.gameId}`}
                  className="flex items-center justify-between gap-3 tt-panel rounded-lg px-3 py-2.5 hover:border-[#3A4859] transition-colors">{inner}</Link>
              ) : (
                <div key={i} data-testid={`player-log-row-plain-${i}`} className="flex items-center justify-between gap-3 tt-panel rounded-lg px-3 py-2.5">{inner}</div>
              );
            })}
          </div>
        ) : <div className="tt-panel rounded-xl p-6 text-center text-white/40 text-sm" data-testid="player-log-empty">{isProcessing ? "Fetching verified stats…" : `No game log entries for ${res.season} yet.`}</div>}
      </section>
    </div>
  );
}
